import React from 'react';
import { MButtonType, MButtonSize, MButtonIconPlacement } from '../MButton/index';
import { type MButtonProps } from '../MButton/MButton.types';
import { MContentSwap } from '../MContentSwap/index';
import { MContentSwapType, MContentSwapAlignment, type MContentSwapProps } from '../MContentSwap/MContentSwap.types';
import { MTextBlockSimple, MTextBlockSimpleAlignment } from '../MTextBlockSimple/index';
import { AHeaderSize, AHeaderTag } from '../../atoms/AHeader/AHeader.types';
import { ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { AEyebrowVariant } from '../../atoms/AEyebrow/AEyebrow.types';
import {
  MContentHeaderAlignment,
  MContentHeaderAllowedButtonType,
  MContentHeaderButton,
  MContentHeaderItem,
  MContentHeaderProps,
} from './MContentHeader.types';
import * as Styled from './MContentHeader.styles';

const COMPONENT_NAME = 'm-web-ui-kit-MContentHeader';
const TEST_ID = 'web-ui-kit-MContentHeader';

const ALLOWED_BUTTON_TYPES = new Set<string>([MButtonType.Secondary, MButtonType.Tertiary]);

function resolveButtonType(requested?: string): MContentHeaderAllowedButtonType {
  if (requested && ALLOWED_BUTTON_TYPES.has(requested)) {
    return requested as MContentHeaderAllowedButtonType;
  }
  if (process.env['NODE_ENV'] !== 'production' && requested) {
    console.warn(
      `[MContentHeader] buttonType "${requested}" is not allowed. Allowed: Secondary, Tertiary. Falling back to Secondary.`
    );
  }
  return MButtonType.Secondary;
}

const mapAlignmentToTextBlock = (alignment?: MContentHeaderAlignment): MTextBlockSimpleAlignment => {
  if (alignment === MContentHeaderAlignment.Centered) {
    return MTextBlockSimpleAlignment.Center;
  }
  return MTextBlockSimpleAlignment.Left;
};

const mapButtonSize = (size?: string): MButtonSize => {
  switch (size?.toUpperCase()) {
    case 'S':
      return MButtonSize.S;
    case 'M':
      return MButtonSize.M;
    case 'L':
      return MButtonSize.L;
    default:
      return MButtonSize.M;
  }
};

const mapIconPlacement = (icon?: string): MButtonIconPlacement | undefined => {
  switch (icon?.toLowerCase()) {
    case 'leading':
      return MButtonIconPlacement.Leading;
    case 'trailing':
      return MButtonIconPlacement.Trailing;
    case 'none':
    default:
      return undefined;
  }
};

/**
 * Resolves the effective raw button for a CTA slot by applying the
 * "flat-prop wins, swap-model fallback" rule once, in one place.
 *
 * Call this at the organism level — before type-mapping — whenever both
 * a flat `mButton` and a `mContentSwap.mButton` may be present. The
 * returned value feeds the organism's existing `toContentHeaderButton`
 * (or equivalent) mapper.
 *
 * @param primary   The flat top-level button prop from the organism's props.
 * @param fallback  The button nested inside `mContentSwap` (raw AEM shape).
 * @see RawContentSwapButton — the shared raw input type organisms can use for `T`.
 */
export function resolveRawButton<T>(primary: T | undefined, fallback: T | undefined): T | undefined {
  return primary ?? fallback;
}

function mapButton({
  buttonType,
  type,
  ctaLink,
  openInANewTab,
  externalLink,
  size,
  icon,
  aIcon,
  ...rest
}: MContentHeaderButton): MButtonProps {
  return {
    ...rest,
    variant: resolveButtonType(buttonType ?? type),
    size: mapButtonSize(size),
    iconPlacement: mapIconPlacement(icon),
    aIcon: aIcon ? { icon: aIcon.icon || '', size: aIcon.size } : undefined,
    href: ctaLink,
    isExternal: openInANewTab === true || openInANewTab === 'true' || externalLink,
  };
}

function resolveContentSwapProps(resolved: MContentHeaderItem): MContentSwapProps | null {
  // An explicit mContentSwap config is only shown when the CTA slot is enabled.
  if (resolved.mContentSwap) {
    return resolved.cta === 'true' ? resolved.mContentSwap : null;
  }

  // Otherwise fall back to a Button-type swap built from the legacy CTA props.
  // When `cta` is explicitly set, honour it as the gate.
  // When a caller (e.g. OCardCarousel) has pre-resolved mButton via resolveRawButton
  // and omitted the `cta` flag, treat a present mButton as implicitly enabled.
  const ctaEnabled = resolved.cta === 'true' || (resolved.cta == null && resolved.mButton != null);
  const firstButton = ctaEnabled && resolved.mButton;
  const secondaryCta = resolved.secondaryCta;
  const secondaryEnabled = secondaryCta === 'true' || (secondaryCta == null && resolved.mButton2 != null);
  const secondButton = secondaryEnabled && resolved.mButton2;
  const legacyButtons = [firstButton, secondButton].filter(Boolean) as MContentHeaderButton[];

  if (legacyButtons.length === 0) {
    return null;
  }

  return {
    type: MContentSwapType.Button,
    mButton: mapButton(legacyButtons[0]),
    mButton2: legacyButtons[1] ? mapButton(legacyButtons[1]) : undefined,
    button2: legacyButtons.length > 1,
  };
}

export const MContentHeader = (props: MContentHeaderProps) => {
  const { mContentHeader, ...flatProps } = props;
  const resolved = { ...mContentHeader, ...flatProps };

  const { mTextBlockSimple, alignment } = resolved;
  const aHeader = mTextBlockSimple?.aHeader;
  const headerText = aHeader?.text;
  if (!mTextBlockSimple || !aHeader || !headerText) return null;

  const resolvedAlignment = alignment || MContentHeaderAlignment.Left;

  const contentSwapProps = resolveContentSwapProps(resolved);

  return (
    <Styled.ContentHeaderWrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      $alignment={resolvedAlignment}
    >
      <Styled.TextBlockWrapper $alignment={resolvedAlignment}>
        <MTextBlockSimple
          alignment={mapAlignmentToTextBlock(resolvedAlignment)}
          eyebrowText={mTextBlockSimple.aEyebrow?.eyebrowText}
          eyebrowVariant={AEyebrowVariant.Primary}
          headerText={headerText}
          headerSize={(aHeader.size as AHeaderSize) || AHeaderSize.TitleL}
          headerTag={(aHeader.headerTag as AHeaderTag) || AHeaderTag.h2}
          bodyText={mTextBlockSimple.aBody?.bodyText}
          bodySize={(mTextBlockSimple.aBody?.size as ABodySize) || ABodySize.M}
          bodyColor={(mTextBlockSimple.aBody?.color as ABodyColor) || ABodyColor.Default}
          aBody={{ className: 'm-ellipsis-3' }}
        />
      </Styled.TextBlockWrapper>
      {contentSwapProps && (
        <MContentSwap
          {...contentSwapProps}
          alignment={
            resolvedAlignment === MContentHeaderAlignment.Centered
              ? MContentSwapAlignment.Centered
              : MContentSwapAlignment.Left
          }
        />
      )}
    </Styled.ContentHeaderWrapper>
  );
};

export type {
  MContentHeaderItem,
  MContentHeaderButton,
  MContentHeaderAllowedButtonType,
  RawContentSwapButton,
} from './MContentHeader.types';
export { MContentHeaderAlignment } from './MContentHeader.types';
